
import { Vue, Options, prop } from "vue-class-component";
import { BagOrStt, BagOrSttDetail } from "@/domain/entities/CargoV2";
import { getScanNotificationSound, playNotification } from "@/app/infrastructures/misc/UtilsAudio";
import { getScanFlags } from "@/domain/entities/ScanFlags";
import { AccountController } from "@/app/ui/controllers/AccountController";
import Icons from "@/app/ui/components/lp-icon/index.vue";
import XLSX from "xlsx";
import ScanTableList from "./scan-table-list.vue";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import {
  CalculateVolumeApiRequestV2,
  CalculateVolumeRequestV2,
  ScanApiRequest
} from "@/data/payload/api/CargoV2ApiRequest";
import {
  CALCULATE_TYPE,
  getConfigDimensionDivider,
  getConfigIsminimumVolumePerPiece,
  getConfigMinimumVolumePerPiece
} from "../modules/cargo-module";
import { OptionsClass, ModalMessageEntities } from "@/domain/entities/MainApp";
import { startCase } from "lodash";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { DEFAULT_INTERNATIONAL_PRODUCT } from "../modules/constants";

class Props {
  fromEdit = prop<boolean>({
    default: false
  });
}

@Options({
  components: {
    Icons,
    ScanTableList
  }
})
class ScanTableSection extends Vue.with(Props) {
  public onScanDone(result: {
    success: boolean;
    item?: BagOrSttDetail;
    items?: BagOrSttDetail[];
    reason?: string;
  }) {
    const scannedItem = result.items?.[0] || result.item;
    if (result.success) {
      this.updateStatusBadge(scannedItem || new BagOrSttDetail());
      this.scanStatus = "success";
      playNotification(
        getScanNotificationSound(scannedItem),
        AccountController.accountData.accountIsForeign
      );
    } else {
      this.scanStatus = "error";
      playNotification("error");
      this.invalidSttList.unshift({
        sttNumber: this.scanInput,
        reason:
          result.reason ||
          this.$t("cargoV2.components.scanTableSection.scanProcessFailed")
      });
    }
    this.scanCounter++;
    this.scanInput = "";
    this.isLoading = false;
  }

  private updateStatusBadge(item: BagOrSttDetail) {
    const flags = getScanFlags(item);
    this.successWithDg = flags.isDangerousGoods;
    this.successWithWoodpacking = flags.isWoodpacking;
    this.successWithBosspack = String(item.productType || "").toUpperCase() === "BOSSPACK";
  }
  scanInput = "";
  scanStatus: "idle" | "success" | "error" = "idle";
  isLoading = false;
  successWithDg = false;
  successWithBosspack = false;
  successWithWoodpacking = false;
  scanCounter = 0;

  private readonly badgeAssets: Record<string, string> = {
    error: require("@/app/ui/assets/svg/scan-cargoV2-badges/error.svg"),
    success: require("@/app/ui/assets/svg/scan-cargoV2-badges/success.svg"),
    successWithBosspack: require("@/app/ui/assets/svg/scan-cargoV2-badges/success-with-bosspack.svg"),
    successWithDg: require("@/app/ui/assets/svg/scan-cargoV2-badges/success-with-dg.svg"),
    successWithDgBosspack: require("@/app/ui/assets/svg/scan-cargoV2-badges/success-with-dg-bosspack.svg"),
    successWithWoodpacking: require("@/app/ui/assets/svg/scan-cargoV2-badges/success-with-woodpacking.svg"),
    successWithWoodpackingEn: require("@/app/ui/assets/svg/scan-cargoV2-badges/success-with-woodpacking-en.svg"),
    successWithWoodpackingBosspack: require("@/app/ui/assets/svg/scan-cargoV2-badges/success-with-woodpacking-bosspack.svg"),
    successWithWoodpackingBosspackEn: require("@/app/ui/assets/svg/scan-cargoV2-badges/success-with-woodpacking-bosspack-en.svg"),
    successWithDgWoodpacking: require("@/app/ui/assets/svg/scan-cargoV2-badges/success-with-dg-woodpacking.svg"),
    successWithDgWoodpackingEn: require("@/app/ui/assets/svg/scan-cargoV2-badges/success-with-dg-woodpacking-en.svg"),
    successWithDgWoodpackingBosspack: require("@/app/ui/assets/svg/scan-cargoV2-badges/success-with-dg-woodpacking-bosspack.svg"),
    successWithDgWoodpackingBosspackEn: require("@/app/ui/assets/svg/scan-cargoV2-badges/success-with-dg-woodpacking-bosspack-en.svg")
  };

  get statusBadgeSrc(): string | null {
    if (this.scanStatus === "error") return this.badgeAssets.error;
    if (this.scanStatus !== "success") return null;
    const language = AccountController.accountData.accountIsForeign ? "En" : "";
    if (this.successWithDg && this.successWithWoodpacking) {
      const badge = this.successWithBosspack
        ? "successWithDgWoodpackingBosspack"
        : "successWithDgWoodpacking";
      return this.badgeAssets[badge + language];
    }
    if (this.successWithDg && this.successWithBosspack)
      return this.badgeAssets.successWithDgBosspack;
    if (this.successWithDg) return this.badgeAssets.successWithDg;
    if (this.successWithWoodpacking) {
      const badge = this.successWithBosspack
        ? "successWithWoodpackingBosspack"
        : "successWithWoodpacking";
      return this.badgeAssets[badge + language];
    }
    if (this.successWithBosspack) return this.badgeAssets.successWithBosspack;
    return this.badgeAssets.success;
  }

  get statusBadgeAlt(): string {
    if (this.scanStatus === "error") return "warning-circle";
    if (this.scanStatus === "success") return "check-circle";
    return "status";
  }

  get statusAnimationClass(): string {
    if (this.scanStatus === "error") return "animate-shake";
    return "animate-bounce-in";
  }

  // Data
  dataList: BagOrSttDetail[] = [];

  invalidSttList: Array<{ sttNumber: string; reason: string }> = [];

  declare $refs: {
    scanInputRef: HTMLInputElement;
  };

  handleDelete(e: Event) {
    this.onDelete((e as CustomEvent).detail);
  }

  private deleteHandler: ((e: Event) => void) | null = null;

  mounted() {
    this.deleteHandler = (e: Event) => this.handleDelete(e);
    document.addEventListener("delete-item", this.deleteHandler);
  }

  beforeUnmount() {
    if (this.deleteHandler) {
      document.removeEventListener("delete-item", this.deleteHandler);
    }
  }

  downloadInvalidSttList() {
    if (!this.invalidSttList.length) return;

    const data = this.invalidSttList.map(
      (d: { sttNumber: string; reason: string }) => ({
        [this.$t(
          "cargoV2.components.scanTableSection.export.sttNumber"
        )]: d.sttNumber,
        [this.$t(
          "cargoV2.components.scanTableSection.export.failedReason"
        )]: d.reason
      })
    );

    const sheet = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      sheet,
      this.$t("cargoV2.components.scanTableSection.invalidSttTitle")
    );
    XLSX.writeFile(
      wb,
      `${this.$t("cargoV2.components.scanTableSection.invalidSttTitle")}.xlsx`
    );
  }

  get form() {
    return CargoV2Controller.form;
  }

  async onScan() {
    if (this.isLoading) return;
    if (!this.scanInput) return;

    this.isLoading = true;
    this.successWithDg = false;
    this.successWithBosspack = false;
    this.successWithWoodpacking = false;
    this.scanStatus = "idle";
    const scanValue = this.scanInput;

    let bagNo = "";
    let sttNo = "";
    const trimmedScanInput = this.scanInput.trim();
    const isBagNumber = /^[a-zA-Z]{3}-\S+/.test(trimmedScanInput);

    if (isBagNumber) {
      bagNo = trimmedScanInput;
    } else {
      sttNo = trimmedScanInput;
    }

    const existingBagNos = new Set<string>();
    const existingSttNos = new Set<string>();
    const existingShipmentIds = new Set<string>();
    const rows = Array.isArray(this.form?.bagOrStt) ? this.form.bagOrStt : [];

    for (const row of rows as any[]) {
      const rowBagNo = String(row?.bagNo || "").trim();
      if (rowBagNo && rowBagNo !== "-") existingBagNos.add(rowBagNo);

      const rowSttNo = String(row?.sttNo || "").trim();
      if (rowSttNo && rowSttNo !== "-") existingSttNos.add(rowSttNo);

      const rowShipmentId = String(
        row?.sttShipmentId || row?.shipmentId || ""
      ).trim();
      if (rowShipmentId && rowShipmentId !== "-")
        existingShipmentIds.add(rowShipmentId);

      const details = Array.isArray(row?.sttDetails) ? row.sttDetails : [];
      for (const d of details) {
        const dBagNo = String(d?.bagNo || "").trim();
        if (dBagNo && dBagNo !== "-") existingBagNos.add(dBagNo);

        const dSttNo = String(d?.sttNo || "").trim();
        if (dSttNo && dSttNo !== "-") existingSttNos.add(dSttNo);

        const dShipmentId = String(
          d?.sttShipmentId || d?.shipmentId || ""
        ).trim();
        if (dShipmentId && dShipmentId !== "-")
          existingShipmentIds.add(dShipmentId);
      }
    }

    if (bagNo && existingBagNos.has(bagNo)) {
      this.onScanDone({
        success: false,
        reason: this.$t("cargoV2.components.scanTableSection.bagAlreadyExists")
      });
      return;
    }

    const isSttDuplicated = sttNo
      ? existingSttNos.has(sttNo) || existingShipmentIds.has(sttNo)
      : false;

    if (isSttDuplicated) {
      this.onScanDone({
        success: false,
        reason: this.$t("cargoV2.components.scanTableSection.sttAlreadyExists")
      });
      return;
    }

    try {
      const {
        cargoType,
        productCargo,
        cargoCommodity,
        destinationCityCode: destinationCity
      } = this.form;
      const res = await CargoV2Controller.scan(
        new ScanApiRequest({
          bagOrStt: new BagOrStt({
            bagNo,
            sttNo
          }),
          cargoProductType: productCargo,
          cargoCommodity: cargoCommodity.code,
          cargoType: cargoType.value
        })
      );

      if (!Array.isArray(res.bagOrStt) || !res.bagOrStt.length) {
        const message = AccountController.accountData.accountIsForeign
          ? res.errorMessageEn
          : res.errorMessage;
        throw new Error(message || "Empty scan result");
      }

      const {
        destinationCityName,
        destinationCityCode,
        destinationCountryCode,
        isPaid
      } = res.bagOrStt[0];

      if (!isPaid) {
        throw new Error(
          this.$t("cargoV2.components.scanTableSection.weightExcess").toString()
        );
      }

      if (res.isAllowUpdateStatus) {
        const duplicateSttInBag = res.bagOrStt.find((item: BagOrSttDetail) => {
          const itemSttNo = String(item?.sttNo || "").trim();
          if (itemSttNo && itemSttNo !== "-" && existingSttNos.has(itemSttNo)) {
            return true;
          }
          if (Array.isArray(item.sttDetails) && item.sttDetails.length) {
            return item.sttDetails.some((d: BagOrSttDetail) => {
              const dSttNo = String(d?.sttNo || "").trim();
              return dSttNo && dSttNo !== "-" && existingSttNos.has(dSttNo);
            });
          }
          return false;
        });

        if (duplicateSttInBag) {
          this.onScanDone({
            success: false,
            reason: this.$t("cargoV2.components.scanTableSection.sttAlreadyExists").toString()
          });
          return;
        }

        if (sttNo) {
          const parentBagAlreadyInTable = res.bagOrStt.find(
            (item: BagOrSttDetail) => {
              const itemBagNo = String(
                item?.bagNo ||
                  item?.baggingNumber ||
                  (item as any)?.sttBagNo ||
                  ""
              ).trim();
              return Boolean(
                itemBagNo && itemBagNo !== "-" && existingBagNos.has(itemBagNo)
              );
            }
          );
          if (parentBagAlreadyInTable) {
            this.onScanDone({
              success: false,
              reason: this.$t("cargoV2.components.scanTableSection.bagAlreadyExists").toString()
            });
            return;
          }
        }

        if (this.fromEdit) {
          this.form.photoProof = [];
          CargoV2Controller.setUploadProofPhoto(false);
        }
        // add action to bagOrStt
        const scannedItems = res.bagOrStt.map((item: BagOrSttDetail) => {
          const mapped = new BagOrSttDetail({
            ...item,
            action: this.fromEdit ? "add" : item.action || ""
          });
          if (Array.isArray(item.sttDetails) && item.sttDetails.length) {
            mapped.sttDetails = item.sttDetails.map(
              (det: BagOrSttDetail) =>
                new BagOrSttDetail({
                  ...det,
                  action: this.fromEdit ? "add" : det.action || ""
                })
            );
          }
          return mapped;
        });

        // generate group by bagNo
        CargoV2Controller.onGenerateGroupDataTable([
          ...scannedItems,
          ...this.form.bagOrStt
        ]);

        // generate dominan commodity group
        CargoV2Controller.onGenerateDominanCommodityGroup();

        // generate default nog
        CargoV2Controller.onGenerateDefaultNog();

        // calculate volume
        this.calculateVolume(CALCULATE_TYPE.INIT);

        // update form
        CargoV2Controller.setForm({
          destinationCityCode: destinationCity.code
            ? destinationCity
            : new OptionsClass({
                label: `${destinationCityCode} - ${startCase(
                  destinationCityName?.toLowerCase()
                )}`,
                code: destinationCityCode,
                name: startCase(destinationCityName?.toLowerCase()),
                countryCode: destinationCountryCode || ""
              }),
          productCargo:
            !destinationCity.code &&
            destinationCountryCode &&
            destinationCountryCode !== "ID"
              ? DEFAULT_INTERNATIONAL_PRODUCT
              : productCargo
        });
        this.onResetWeight();

        this.onScanDone({
          success: true,
          items: scannedItems
        });
        return;
      }

      this.onScanDone({
        success: false,
        reason:
          res.errorMessage ||
          this.$t("cargoV2.components.scanTableSection.scanProcessFailed")
      });
    } catch (e) {
      this.scanInput = scanValue;
      this.onScanDone({
        success: false,
        reason:
          (e as Error).message ||
          this.$t("cargoV2.components.scanTableSection.scanProcessFailed")
      });
    }
  }

  onDelete(itemToDelete: BagOrSttDetail) {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: this.$t("cargoV2.components.scanTableSection.deleteModal.title"),
        message: this.$t(
          "cargoV2.components.scanTableSection.deleteModal.message"
        ),
        textSuccess: this.$t(
          "cargoV2.components.scanTableSection.deleteModal.confirm"
        ),
        textCancel: this.$t("cargoV2.components.common.cancel"),
        onSubmit: () => {
          MainAppController.closeMessageModal();

          if (this.fromEdit) {
            itemToDelete.action = "remove";
            this.form.photoProof = [];
            CargoV2Controller.setUploadProofPhoto(false);
            if (itemToDelete.sttDetails) {
              itemToDelete.sttDetails.forEach((d) => (d.action = "remove"));
            }
          }

          // Filter from form.bagOrStt which is the source of truth for the table
          const newList = this.form.bagOrStt.filter((i) => i !== itemToDelete);

          CargoV2Controller.setForm({
            bagOrStt: newList
          });

          // Also update local dataList to keep it in sync if possible, or just for consistency
          this.dataList = this.dataList.filter((i) => i !== itemToDelete);

          // generate dominan commodity group
          CargoV2Controller.onGenerateDominanCommodityGroup();

          // generate default nog
          CargoV2Controller.onGenerateDefaultNog();

          // calculate volume
          this.calculateVolume(CALCULATE_TYPE.INIT);

          this.onResetWeight();
        },
        onClose: () => {
          MainAppController.closeMessageModal();
        }
      })
    );
  }

  async calculateVolume(calculateType = "") {
    await CargoV2Controller.calculateVolume(
      new CalculateVolumeApiRequestV2({
        calculateType: !this.form.bagOrStt.length ? "INIT" : calculateType,
        calculateRequest: new CalculateVolumeRequestV2({
          cargoType: this.form.cargoType.value,
          chargeableWeightReweigh: this.form.cargoTotalEstimateChargeableWeight,
          currentGrossWeight: this.form
            .totalAccumulationEstimationGrossWeightDataTable,
          height: this.form.actualDimension.cargoActualHeightDimension,
          length: 90,
          width: 60,
          totalPieces: this.form.totalAccumulationActualPieceDataTable,
          volumeWeightReweigh: this.form
            .totalAccumulationEstimationVolumeWeightDataTable,
          divider: getConfigDimensionDivider(this.form.cargoType.value),
          isMinimumVolumePerPiece: getConfigIsminimumVolumePerPiece(
            this.form.cargoType.value,
            this.form.bookingPayload.awbNo ? "auto" : ""
          ),
          minVolumePerPiece: getConfigMinimumVolumePerPiece(
            this.form.cargoType.value,
            this.form.bookingPayload.awbNo ? "auto" : ""
          )
        })
      })
    );
  }

  onResetWeight() {
    CargoV2Controller.setForm({
      actualTotalCargoGrossweigth: this.form
        .totalAccumulationEstimationGrossWeightDataTable,
      actualTotalCargoVolumeweight: this.form
        .totalAccumulationEstimationVolumeWeightDataTable,
      estTotalCargoGrossweigth: this.form
        .totalAccumulationEstimationGrossWeightDataTable,
      estTotalCargoVolumeweight: this.form
        .totalAccumulationEstimationVolumeWeightDataTable,
      cargoActualPiece: this.form.totalAccumulationActualPieceDataTable
    });
  }
}

export default ScanTableSection;
